/*
 * Author: Blue Lobster Private Limited
 * Copyright: Blue Lobster Private Limited
 */
import { lazy } from "react";

/**
 * A wrapper around React.lazy that retries the import if it fails due to chunk loading errors.
 * This is common after a new deployment when old chunks are no longer available.
 *
 * @param {Function} componentImport - The dynamic import function, e.g. () => import('./MyComponent')
 * @returns {React.LazyExoticComponent} - The lazy loaded component
 */
export const lazyWithRetry = (componentImport) => {
	return lazy(async () => {
		// If we're not in a browser (SSR/tests), just do a plain import with no reload logic.
		if (typeof window === "undefined") {
			return componentImport();
		}

		let pageHasAlreadyBeenForceRefreshed = false;

		// Be defensive about sessionStorage (privacy mode / disabled storage, etc.)
		try {
			pageHasAlreadyBeenForceRefreshed = JSON.parse(
				window.sessionStorage.getItem("page-has-been-force-refreshed") ||
					"false",
			);
		} catch {
			pageHasAlreadyBeenForceRefreshed = false;
		}

		try {
			const component = await componentImport();
			// If successful, clear the flag so future errors can trigger a reload
			try {
				window.sessionStorage.removeItem("page-has-been-force-refreshed");
			} catch {
				// ignore storage errors
			}
			return component;
		} catch (error) {
			const message = error?.message || "";
			const name = error?.name || "";

			// Check for common chunk load / dynamic import failures
			// Vite often throws "Unable to preload CSS" or generic network errors for missing chunks
			const isChunkLoadError =
				message.includes("Unable to preload CSS") ||
				message.includes("Loading chunk") ||
				message.includes("Failed to fetch dynamically imported module") ||
				message.includes("error loading dynamically imported module") ||
				name === "ChunkLoadError";

			if (isChunkLoadError && !pageHasAlreadyBeenForceRefreshed) {
				// Mark that we are reloading to avoid infinite loops
				try {
					window.sessionStorage.setItem(
						"page-has-been-force-refreshed",
						"true",
					);
				} catch {
					// ignore storage errors
				}

				// Trigger hard reload so the browser fetches the latest chunks
				window.location.reload();

				// Keep Suspense pending while the page reloads; React will be torn down anyway
				return new Promise(() => {
					// Intentionally empty to keep promise pending
				});
			}

			// If we already reloaded or it's a different error, rethrow
			throw error;
		}
	});
};
